import React, { useState, useRef, useEffect } from "react";
import { IonButton } from "@ionic/react";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const FoodItems: Record<string, string[]> = {
    "Fruits": ["Apple", "Banana", "Mango", "Orange", "Grapes", "Pineapple", "Strawberry", "Papaya", "Watermelon", "Cherry"],
    "Vegetables": ["Carrot", "Broccoli", "Spinach", "Tomato", "Cucumber", "Lettuce", "Cabbage", "Potato", "Onion", "Peas"],
    "Dairy": ["Milk", "Cheese", "Butter", "Yogurt", "Cream", "Paneer", "Ghee", "Buttermilk"],
    "Meat & Seafood": ["Chicken", "Fish", "Mutton", "Prawns", "Crab", "Beef", "Lamb", "Turkey"],
    "Grains & Cereals": ["Rice", "Wheat", "Oats", "Barley", "Corn", "Quinoa", "Millet", "Rye"],
    "Nuts & Seeds": ["Almonds", "Cashews", "Walnuts", "Peanuts", "Pistachios", "Chia Seeds", "Flaxseeds", "Sunflower Seeds"],
    "Beverages": ["Tea", "Coffee", "Milkshake", "Juice", "Smoothie", "Coconut Water", "Soda", "Lemonade"],
    "Sweets & Snacks": ["Chocolate", "Cake", "Biscuits", "Chips", "Cookies", "Candy", "Pastry", "Donut", "Ice Cream"],
    "Spices": ["Salt", "Sugar", "Black Pepper", "Turmeric", "Cinnamon", "Chili Powder", "Ginger", "Garlic"],
    "Pulses": ["Lentils", "Chickpeas", "Kidney Beans", "Black Beans", "Peas", "Soybeans", "Split Peas"],
    "Oils & Fats": ["Olive Oil", "Coconut Oil", "Sunflower Oil", "Butter", "Ghee", "Vegetable Oil", "Mustard Oil"]
};


const MatchFoodItems: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024)
        {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch questions dynamically
    const fetchQuestions = () => {
        const usedKeys: string[] = [];
        const homeKeys = Object.keys(FoodItems);
        const shuffledKeys = shuffleArray([...homeKeys]);
        const selectedKeys = shuffledKeys.slice(0, 4); console.log(selectedKeys)
        const pairs = selectedKeys.map((key, index) => {
            const images = FoodItems[key];
            const randomImage = images[Math.floor(Math.random() * images.length)];
            usedKeys.push(randomImage);
        });
        const newQuestions: ImagePair[] = [];

        const suffledNumbers = shuffleArray([...usedKeys]); //console.log(suffledNumbers)
        selectedKeys.map((key, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: usedKeys.indexOf(suffledNumbers[index]) + 1,
                leftImage: key,
                rightImage: suffledNumbers[index],
            });
        })

        //console.log(newQuestions)

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Mamas Guidance - Class One - Environment Science - My Needs – Food - Match Food Items"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            {!showImage ? (
                <div ref={containerRef} className="contentPart additionWord matchImage match-container foodItems" style={{ position: "relative", height: "calc(100vh - 200px)" }}>{/* SVG for drawing lines */}
                    <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                        {connections.map((conn, index) => (
                            <line
                                key={index}
                                x1={conn.x1}
                                y1={conn.y1}
                                x2={conn.x2}
                                y2={conn.y2}
                                stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                strokeWidth="3"
                            />
                        ))}
                    </svg>
                    <div className="columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                        <div className="left-column">
                            {questions.map((item) => (
                                <div
                                    key={item.left_id}
                                    className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                    onClick={(e) => handleLeftClick(e, item.left_id)}
                                    style={{ cursor: "pointer", padding: "10px" }}
                                >
                                    {item.leftImage}
                                </div>
                            ))}
                        </div>
                        <div className="right-column">
                            {questions.map((item) => (
                                <div key={item.left_id} className="image-box " onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    {item.rightImage}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchFoodItems;
